/**
 * Unauthorized Page for trAInedup.ai
 * Displayed when users try to access resources they don't have permission for
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { ROUTES } from '../utils/constants';

/**
 * Unauthorized page component
 * Shows appropriate message based on the type of access restriction
 */
export const UnauthorizedPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as {
    from?: Location;
    requiredRole?: string;
    userRoles?: string[];
    message?: string;
  } | null;

  const requiredRole = state?.requiredRole;
  const userRoles = state?.userRoles;
  const customMessage = state?.message;

  /**
   * Get appropriate message based on the access restriction
   */
  const getMessage = () => {
    if (customMessage) {
      return customMessage;
    }

    if (requiredRole && userRoles) {
      return `This page requires ${requiredRole} access. Your current roles: ${userRoles.join(', ')}`;
    }

    return 'You do not have permission to access this page.';
  };

  /**
   * Get appropriate title based on the access restriction
   */
  const getTitle = () => {
    if (requiredRole) {
      return `${requiredRole} Access Required`;
    }

    return 'Access Denied';
  };

  /**
   * Get suggested actions based on user role
   */
  const getSuggestedActions = () => {
    if (requiredRole && userRoles) {
      if (requiredRole === 'admin' && !userRoles.includes('admin')) {
        return (
          <>
            <p className="mb-2">You need admin privileges to access this page.</p>
            <p className="mb-2">Contact your administrator if you believe this is a mistake.</p>
          </>
        );
      }
      if (requiredRole === 'instructor' && !userRoles.includes('instructor')) {
        return (
          <>
            <p className="mb-2">You need instructor privileges to access this page.</p>
            <p className="mb-2">Contact support if you believe this is a mistake.</p>
          </>
        );
      }
    }
    return null;
  };

  const suggestedActions = getSuggestedActions();
  // Ensure suggestedActions is always an array for mapping
  const suggestedActionsArray = Array.isArray(suggestedActions)
    ? suggestedActions
    : suggestedActions
    ? [suggestedActions]
    : [];

  return (
    <Layout>
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-red-600" />
            </div>

            {/* Title */}
            <h1 className="font-montserrat font-bold text-2xl text-neutral-800 mb-4">
              {getTitle()}
            </h1>

            {/* Message */}
            <p className="text-neutral-600 mb-8">
              {getMessage()}
            </p>

            {/* Role Information */}
            {requiredRole && userRoles && (
              <>
                <span className="text-neutral-600">Required Role:</span>
                <span className="font-medium text-neutral-800 capitalize">{requiredRole}</span>
                <span className="mx-2 text-neutral-400">|</span>
                <span className="text-neutral-600">Your Roles:</span>
                <span className="font-medium text-neutral-800 capitalize">{userRoles.join(', ')}</span>
              </>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {suggestedActionsArray.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Link key={index} to={action.href}>
                    <Button
                      variant={action.variant}
                      size="lg"
                      fullWidth
                      icon={Icon}
                    >
                      {action.text}
                    </Button>
                  </Link>
                );
              })}

              {/* Go Back Button */}
              <Button
                variant="ghost"
                size="lg"
                fullWidth
                icon={ArrowLeft}
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-neutral-200">
              <p className="text-sm text-neutral-500">
                If you believe this is an error, please{' '}
                <Link
                  to={ROUTES.contact}
                  className="text-primary hover:text-primary-600 underline"
                >
                  contact our support team
                </Link>
                .
              </p>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

